import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, X, ArrowRight, Building2, Users, Crown, Zap, Shield, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Logo from '../components/Logo';

const PLANS = [
  {
    name: 'Basic',
    tagline: 'Get started for free',
    price: 'Free',
    priceNote: 'No monthly fees',
    annualPrice: null,
    color: '#A1A1AA',
    features: [
      { text: 'Up to 5 property listings', included: true },
      { text: 'Property detail page', included: true },
      { text: 'AI brochure extraction', included: false },
      { text: 'Lead notifications (count only)', included: true },
      { text: 'Lead details access', included: false, note: 'Recharge credits (10 per lead)' },
      { text: 'Analytics dashboard', included: false },
      { text: 'Dedicated upload support', included: false },
      { text: 'Priority placement', included: false },
    ],
    cta: 'Start Free',
    popular: false,
  },
  {
    name: 'Professional',
    tagline: 'For growing partners',
    price: '$399',
    priceNote: '/month',
    annualPrice: '$3,990/year',
    annualSaving: 'Save ~17%',
    color: '#D4AF37',
    features: [
      { text: 'Up to 25 property listings', included: true },
      { text: 'Property detail page', included: true },
      { text: 'AI brochure extraction', included: true },
      { text: 'Lead notifications', included: true },
      { text: '500 lead credits/month (20 per listing)', included: true },
      { text: 'Basic analytics', included: true },
      { text: 'Dedicated upload support', included: false },
      { text: 'Priority placement', included: false },
    ],
    cta: 'Start Onboarding',
    popular: true,
  },
  {
    name: 'Enterprise',
    tagline: 'For top-tier developers',
    price: '$999',
    priceNote: '/month',
    annualPrice: '$9,990/year',
    annualSaving: 'Save ~17%',
    color: '#8b5cf6',
    features: [
      { text: 'Unlimited property listings', included: true },
      { text: 'Property detail page', included: true },
      { text: 'AI brochure extraction', included: true },
      { text: 'Lead notifications', included: true },
      { text: 'Unlimited lead access', included: true },
      { text: 'Full analytics dashboard', included: true },
      { text: 'Dedicated upload support', included: true },
      { text: 'Priority placement & branding', included: true },
    ],
    cta: 'Start Onboarding',
    popular: false,
  },
];

export default function PartnerPlansPage() {
  return (
    <div className="min-h-screen bg-[#0B0C10]">
      {/* Header */}
      <header className="bg-[#1A1D24] border-b border-[#2A2F3A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <Logo size="small" showText={true} />
            </Link>
            <Link to="/partner">
              <Button variant="outline" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10">
                Partner Login
              </Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-20 text-center">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto px-4">
          <Badge className="bg-[#D4AF37]/10 text-[#D4AF37] border-[#D4AF37]/30 mb-6">Partner Program</Badge>
          <h1 className="text-4xl sm:text-5xl text-white mb-6 leading-tight" data-testid="plans-heading">
            List Your Properties.<br />
            <span className="text-[#D4AF37]">Reach Global Investors.</span>
          </h1>
          <p className="text-[#A1A1AA] text-lg max-w-2xl mx-auto">
            Join Mulberry World's partner network and connect with high-net-worth Indian investors looking for international residency opportunities.
          </p>
        </motion.div>
      </section>

      {/* Plans Grid */}
      <section className="pb-24 px-4">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANS.map((plan, i) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className={`relative bg-[#1A1D24] border rounded-2xl p-8 flex flex-col ${plan.popular ? 'border-[#D4AF37] ring-1 ring-[#D4AF37]/20' : 'border-[#2A2F3A]'}`}
              data-testid={`plan-${plan.name.toLowerCase()}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-[#D4AF37] text-black text-xs px-3">Most Popular</Badge>
                </div>
              )}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${plan.color}15` }}>
                    {plan.name === 'Basic' && <Building2 className="h-4 w-4" style={{ color: plan.color }} />}
                    {plan.name === 'Professional' && <Zap className="h-4 w-4" style={{ color: plan.color }} />}
                    {plan.name === 'Enterprise' && <Crown className="h-4 w-4" style={{ color: plan.color }} />}
                  </div>
                  <h3 className="text-xl text-white font-semibold">{plan.name}</h3>
                </div>
                <p className="text-[#A1A1AA] text-sm">{plan.tagline}</p>
              </div>

              <div className="mb-8">
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl text-white font-bold">{plan.price}</span>
                  {plan.priceNote && <span className="text-[#A1A1AA] text-sm">{plan.priceNote}</span>}
                </div>
                {plan.annualPrice && (
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[#A1A1AA] text-xs">or {plan.annualPrice}</span>
                    {plan.annualSaving && <Badge className="bg-green-500/20 text-green-400 border-0 text-[10px]">{plan.annualSaving}</Badge>}
                  </div>
                )}
              </div>

              <div className="flex-1 space-y-3 mb-8">
                {plan.features.map((f, j) => (
                  <div key={j} className="flex items-start gap-2.5">
                    {f.included ? (
                      <Check className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: plan.color }} />
                    ) : (
                      <X className="h-4 w-4 mt-0.5 text-[#2A2F3A] flex-shrink-0" />
                    )}
                    <div>
                      <span className={`text-sm ${f.included ? 'text-white' : 'text-[#A1A1AA]/50'}`}>{f.text}</span>
                      {f.note && <span className="text-[#A1A1AA] text-xs block">{f.note}</span>}
                    </div>
                  </div>
                ))}
              </div>

              <Link to={`/partner/onboard?tier=${plan.name}&cycle=monthly`} className="block">
                <Button
                  className={`w-full h-11 ${plan.popular ? 'btn-luxury' : 'bg-[#2A2F3A] text-white hover:bg-[#3A3F4A]'}`}
                  data-testid={`plan-cta-${plan.name.toLowerCase()}`}
                >
                  {plan.cta} <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-[#1A1D24] border-t border-[#2A2F3A]">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl text-white text-center mb-12">Detailed Comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="plans-comparison-table">
              <thead>
                <tr className="border-b border-[#2A2F3A]">
                  <th className="text-left text-[#A1A1AA] py-3 px-4 w-1/3">Feature</th>
                  <th className="text-center text-white py-3 px-4">Basic</th>
                  <th className="text-center py-3 px-4"><span className="text-[#D4AF37]">Professional</span></th>
                  <th className="text-center text-white py-3 px-4">Enterprise</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2A2F3A]/50">
                {[
                  ['Max Listings', '5', '25', 'Unlimited'],
                  ['Lead Credits', '0 (buy credits)', '50 per listing', 'Unlimited'],
                  ['Cost per Lead', '10 credits', '10 credits', 'Free'],
                  ['AI PDF Extraction', 'Yes', 'Yes', 'Yes'],
                  ['Analytics', 'No', 'Basic', 'Full'],
                  ['Lead Details', 'Locked', 'Included', 'Included'],
                  ['Dedicated Support', 'No', 'No', 'Yes'],
                  ['Priority Placement', 'No', 'No', 'Yes'],
                  ['Branding on Listings', 'Basic', 'Enhanced', 'Premium'],
                  ['Pricing', 'Free', '$399/mo or $3,990/yr', '$999/mo or $9,990/yr'],
                ].map(([feature, basic, pro, enterprise], i) => (
                  <tr key={i}>
                    <td className="text-[#A1A1AA] py-3 px-4">{feature}</td>
                    <td className="text-center text-white py-3 px-4">{basic}</td>
                    <td className="text-center text-[#D4AF37] py-3 px-4">{pro}</td>
                    <td className="text-center text-white py-3 px-4">{enterprise}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20">
        <div className="max-w-2xl mx-auto px-4 text-center">
          <Shield className="h-10 w-10 text-[#D4AF37] mx-auto mb-4" />
          <h2 className="text-2xl text-white mb-4">Ready to Partner With Us?</h2>
          <p className="text-[#A1A1AA] mb-8">
            Contact our partnerships team to discuss the best plan for your business. We'll help you get set up and start reaching investors.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href="mailto:info@mulberryleaf.in">
              <Button className="btn-luxury px-8 h-11">
                <Mail className="h-4 w-4 mr-2" /> Email Us
              </Button>
            </a>
            <a href="tel:+918999570132">
              <Button variant="outline" className="border-[#2A2F3A] text-white hover:bg-[#2A2F3A] px-8 h-11">
                <Phone className="h-4 w-4 mr-2" /> Call Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
